// src/services/mentorship.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface MentorshipRequest {
  id: string;
  memberId: string;
  expertId: string;
  memberName: string;
  memberCompany?: string;
  memberTitle?: string;
  requestMessage: string;
  status: 'pending' | 'accepted' | 'rejected';
  rejectedReason?: string;
  createdAt: string;
  respondedAt?: string;
  member?: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
  };
  expert?: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
  };
}

export interface UserSubscription {
  id: string;
  userId: string;
  plan: 'free' | 'pro';
  maxExpertsPerMonth: number;
  maxMessagesPerExpert: number;
  priceSar: number;
  startedAt: string;
  expiresAt?: string;
  autoRenew: boolean;
}

export interface MessageLimits {
  canSend: boolean;
  currentCount: number;
  maxCount: number;
  remaining: number;
}

export interface MentorshipStats {
  subscription: UserSubscription;
  expertsUsed: number;
  expertsLimit: number;
  messageCounts: Array<{
    id: string;
    memberId: string;
    expertId: string;
    messageCount: number;
    monthYear: string;
    expert: {
      id: string;
      firstName: string;
      lastName: string;
      photoUrl?: string;
    };
  }>;
  messagesLimit: number;
}

// ============================================
// MENTORSHIP REQUEST ENDPOINTS
// ============================================

/**
 * Create a mentorship request (member sends to expert)
 */
export async function createMentorshipRequest(
  expertId: string,
  requestMessage: string
): Promise<MentorshipRequest> {
  const { data } = await api.post('/api/mentorship/requests', {
    expertId,
    requestMessage
  });
  return data.data.request;
}

/**
 * Get my mentorship requests
 * - For experts: requests sent to them
 * - For members: requests they've sent
 */
export async function getMyRequests(status?: string): Promise<MentorshipRequest[]> {
  const { data } = await api.get('/api/mentorship/requests', {
    params: { status }
  });
  return data.data.requests;
}

/**
 * Accept a mentorship request (expert only)
 */
export async function acceptMentorshipRequest(requestId: string): Promise<MentorshipRequest> {
  const { data } = await api.post(`/api/mentorship/requests/${requestId}/accept`);
  return data.data.request;
}

/**
 * Reject a mentorship request (expert only)
 */
export async function rejectMentorshipRequest(
  requestId: string,
  reason?: string
): Promise<MentorshipRequest> {
  const { data } = await api.post(`/api/mentorship/requests/${requestId}/reject`, { reason });
  return data.data.request;
}

// ============================================
// MESSAGING & LIMITS ENDPOINTS
// ============================================

/**
 * Check if user can message an expert
 */
export async function checkMessageAccess(expertId: string): Promise<{
  canMessage: boolean;
  isExpert?: boolean;
  reason?: string;
  limits?: MessageLimits;
}> {
  const { data } = await api.get(`/api/mentorship/check-access/${expertId}`);
  return data.data;
}

// ============================================
// SUBSCRIPTION ENDPOINTS
// ============================================

/**
 * Get subscription details for current user
 */
export async function getMySubscription(): Promise<UserSubscription> {
  const { data } = await api.get('/api/mentorship/subscription');
  return data.data.subscription;
}

/**
 * Get mentorship stats for member
 */
export async function getMyStats(): Promise<MentorshipStats> {
  const { data } = await api.get('/api/mentorship/stats');
  return data.data;
}
